'use strict';

Object.defineProperty(exports, '__esModule', { value: true });

/**
 * Notes Module.
 * @module notes
 */

const NOTE_A = 'A';
const NOTE_A_SHARP = 'A#';
const NOTE_A_FLAT = 'Ab';

const NOTE_B = 'B';
const NOTE_B_FLAT = 'Bb';

const NOTE_C = 'C';
const NOTE_C_SHARP = 'C#';

const NOTE_D = 'D';
const NOTE_D_SHARP = 'D#';
const NOTE_D_FLAT = 'Db';

const NOTE_E = 'E';
const NOTE_E_FLAT = 'Eb';

const NOTE_F = 'F';
const NOTE_F_SHARP = 'F#';

const NOTE_G = 'G';
const NOTE_G_SHARP = 'G#';
const NOTE_G_FLAT = 'Gb';

/**
 * Gives chromatic notes starting from C
 */
const CHROMATIC_NOTES = [
  NOTE_C,
  [NOTE_C_SHARP, NOTE_D_FLAT],
  NOTE_D,
  [NOTE_D_SHARP, NOTE_E_FLAT],
  NOTE_E,
  NOTE_F,
  [NOTE_F_SHARP, NOTE_G_FLAT],
  NOTE_G,
  [NOTE_G_SHARP, NOTE_A_FLAT],
  NOTE_A,
  [NOTE_A_SHARP, NOTE_B_FLAT],
  NOTE_B,
];

/**
 * Returns boolean value if octave number is present in note
 * @example:
 * containsOctaveNumber('C4')  //true
 * containsOctaveNumber('F#5')  //true
 * containsOctaveNumber('Db3')  //true
 * containsOctaveNumber('F')  //false
 * @function
 * @param {string} note note in string
 * @returns {boolean}
 */
const containsOctaveNumber = (note) => /[0-9]/.test(note);

/**
 * Returns index of a note from `CHROMATIC_NOTES`.
 * @example:
 * getNoteIndex('C')  //0
 * getNoteIndex('F#')  //6
 * getNoteIndex('Db')  //1
 * getNoteIndex('F')  //5
 * @function
 * @param {string} searchNote note in string
 * @returns {number} Number from 0-11
 */
const getNoteIndex = (searchNote) => {
  let searchIndex = CHROMATIC_NOTES.indexOf(searchNote);

  // if the note is not the one with inharmonic counter part search directly
  if (searchIndex !== -1) {
    return searchIndex;
  }

  CHROMATIC_NOTES.forEach((note, i) => {
    // if is inharmonic val is an array
    if (Array.isArray(note)) {
      if (note.indexOf(searchNote) !== -1) {
        searchIndex = i;
      }
    }
  });

  return searchIndex;
};

/**
 * Octave Module.
 * @module octave
 */

const C_NOTE_INDEX = 0;
const C_SHARP_NOTE_INDEX = 1;

/*
 * checks if octave is changed to another with relativeToC.
 * @function
 * @param {number} currentNoteIndex
 * @returns {boolean}
 */
const octaveChangeRelativeC = (currentNoteIndex) =>
  currentNoteIndex === C_NOTE_INDEX || currentNoteIndex === C_SHARP_NOTE_INDEX;

// pentatonic scale, hexatonic scale, heptatonic scale, octatonic scale
const getNumberOfOctavesFromScale = (scale) => {
  if (scale.length < 8) {
    return 1;
  }

  if ((scale.length - 1) % 5 === 0) {
    // pentatonic scale
    return (scale.length - 1) / 5;
  }

  if ((scale.length - 1) % 6 === 0) {
    // hexatonix scale
    return (scale.length - 1) / 6;
  }

  if ((scale.length - 1) % 8 === 0) {
    // octatonic scale
    return (scale.length - 1) / 8;
  }

  // default return heptatonic scale
  return (scale.length - 1) / 7;
};

/**
 * Get root note index of a chord adjusting inversion
 * @function
 * @param {object} {chord,inversion} options object
 * @param {number} options.chord
 * @param {number} options.inverions
 * @returns {number}
 */
const getRootNoteIndex = ({ chord, inversion }) => {
  // const totalNotes = chord.length;
  if (inversion === 0) {
    return getNoteIndex(chord[0]);
  }
  return getNoteIndex(chord[chord.length - inversion]);
};

/**
 * Append octave number to note or scale
 * This method should also support
 * In C dorian mode, starting node is D, the last two notes, C and D changes to another octave
 * @function
 * @param {object} {note,scale,octave,numberOfOctaves,objectResult} options object
 * @param {string} options.note
 * @param {array} options.scale
 * @param {number} options.octave
 * @param {number} options.numberOfOctaves
 * @param {boolean} options.objectResult
 * @returns {array}
 */
const appendOctave = ({
  note,
  scale,
  octave = 4,
  /* mode = 1, */ numberOfOctaves,
  objectResult,
}) => {
  // append octave for a note
  if (note) {
    if (objectResult) {
      return { note, octave };
    }

    if (containsOctaveNumber(note)) {
      return note;
    }
    return note + octave;
  }

  let totalOctaves = numberOfOctaves;
  if (!numberOfOctaves) {
    totalOctaves = getNumberOfOctavesFromScale(scale);
  }

  if (scale && !Array.isArray(scale)) {
    throw new Error('scale should be an array');
  }

  // append octave for scale
  const firstNoteIndex = getNoteIndex(scale[0]);
  // octave at which scale start
  // const rootOctave = octave;
  const modulo = (scale.length - 1) / totalOctaves;
  let octaveChanged = false;
  let currentOctave = octave;
  return scale.map((scaleNote, index) => {
    const currentNoteIndex = getNoteIndex(scaleNote);

    if (index !== 0 && currentNoteIndex <= firstNoteIndex) {
      // change octave only once in 8 notes
      if (octaveChanged === false) {
        currentOctave += 1;
        octaveChanged = true;
      }
      // reset octaveChanged every octave
      if (index % modulo === 0) {
        octaveChanged = false;
      }
    }
    if (objectResult) {
      return { note: scaleNote, octave: currentOctave };
    }
    return scaleNote + currentOctave;
  });
};

/**
 * Append octave to chord with inversion
 * only works for triad, sixth, seventh chords
 * @function
 * @param {object} {chord,octave,inversion,objectResult} options object
 * @param {array} options.chord
 * @param {number} options.octave
 * @param {number} options.inversion
 * @param {boolean} options.objectResult
 * @returns {array}
 */
const appendOctaveChord = ({
  chord,
  octave = 4,
  inversion = 0,
  objectResult,
}) => {
  const firstNoteIndex = getNoteIndex(chord[0]);
  const rootNoteIndex = getRootNoteIndex({ chord, inversion });
  let currentOctave = octave;
  // const rootOctave = octave;
  let prevNoteIndex = 0;
  let octaveChanged = false;
  return chord.map((note, index) => {
    const currentNoteIndex = getNoteIndex(note);
    if (index !== 0) {
      prevNoteIndex = getNoteIndex(chord[index - 1]);
    }

    // if note reaches C of another octave
    // if currentNoteIndex is less than previous note index or less than firstNoteIndex
    // if first note index less than root note index
    if (
      (index !== 0 || firstNoteIndex < rootNoteIndex) &&
      (currentNoteIndex <= firstNoteIndex || currentNoteIndex < prevNoteIndex)
    ) {
      // change octave only once in 8 notes
      if (octaveChanged === false) {
        currentOctave += 1;
        octaveChanged = true;
      }
      // reset octaveChanged every 7 note of a chord
      if (index % 3 === 0) {
        octaveChanged = false;
      }
    }
    // octave change logic
    // if note reaches C of another octave
    // if currentNoteIndex is less than previous note index or less than firstNoteIndex
    //  if ((((currentNoteIndex < firstNoteIndex || currentNoteIndex < prevNoteIndex)
    //   && (currentOctave - rootOctave < 1))
    //   || (rootNoteIndex !== 0 && octaveChangeRelativeC(currentNoteIndex))
    //   || (rootNoteIndex !== 0 && currentNoteIndex < rootNoteIndex))
    //   && (currentOctave - rootOctave < 1)) {
    //   currentOctave += 1;
    // }

    if (objectResult) {
      return { note, octave: currentOctave };
    }
    return note + currentOctave;
  });
};

/**
 * Append octave to progression
 * @function
 * @param {object} {progression,octave,objectResult} options object
 * @param {array} options.chord
 * @param {number} options.octave
 * @param {boolean} options.objectResult
 * @returns {array}
 */
const appendOctaveProgression = ({
  progression,
  octave = 4,
  objectResult,
}) => {
  // if progression is plain array or chords
  if (Array.isArray(progression[0])) {
    return progression.map((chord) => {
      // if null return the same
      if (chord === null) return chord;
      return appendOctaveChord({ chord, octave, objectResult });
    });
  }

  return progression.map((chord) => {
    // if chord is object { chord, inversion, octave, repetition, quality }
    if (chord.rest) {
      return chord;
    }

    const result = {
      chord: appendOctaveChord({
        octave,
        ...chord,
        objectResult,
      }),
      quality: chord.quality,
      inversion: chord.inversion,
    };
    if (chord.repetition) {
      result.repetition = chord.repetition;
    }
    if (chord.chordPosition !== undefined && chord.chordPosition !== null) {
      result.chordPosition = chord.chordPosition;
    }
    return result;
  });
};

exports.appendOctave = appendOctave;
exports.appendOctaveChord = appendOctaveChord;
exports.appendOctaveProgression = appendOctaveProgression;
exports.getNumberOfOctavesFromScale = getNumberOfOctavesFromScale;
exports.getRootNoteIndex = getRootNoteIndex;
exports.octaveChangeRelativeC = octaveChangeRelativeC;
